import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import { analyticsAPI, leadsAPI } from "../../services/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from "recharts";
import "./Admin.css";

const COLORS = ["#667eea", "#764ba2", "#e74c3c", "#2ecc71", "#f39c12", "#3498db"];

const Dashboard = () => {
  const [metrics, setMetrics] = useState({
    totalLeads: 0, totalLoans: 0, totalCards: 0, newLeads: 0,
    approvedLoans: 0, pendingLoans: 0, approvedCards: 0, pendingCards: 0,
  });
  const [loanMetrics, setLoanMetrics] = useState([]);
  const [cardMetrics, setCardMetrics] = useState({ metrics: [], bankMetrics: [] });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, loanRes, cardRes, leadsRes] = await Promise.all([
        analyticsAPI.getDashboardMetrics(),
        analyticsAPI.getLoanMetrics(),
        analyticsAPI.getCardMetrics(),
        leadsAPI.getLeads("page=1&limit=10"),
      ]);
      setMetrics(dashRes.data);
      setLoanMetrics(loanRes.data);
      setCardMetrics(cardRes.data);
      setRecentLeads(leadsRes.data.leads || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Dashboard">
      {loading ? (
        <div className="loading">Cargando datos...</div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-value">{metrics.totalLeads}</div>
              <div className="metric-label">Total Leads</div>
            </div>
            <div className="metric-card highlight-blue">
              <div className="metric-value">{metrics.newLeads}</div>
              <div className="metric-label">Leads Nuevos</div>
            </div>
            <div className="metric-card highlight-green">
              <div className="metric-value">{metrics.totalLoans}</div>
              <div className="metric-label">Préstamos</div>
            </div>
            <div className="metric-card highlight-purple">
              <div className="metric-value">{metrics.totalCards}</div>
              <div className="metric-label">Tarjetas</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.approvedLoans}</div>
              <div className="metric-label">Préstamos Aprobados</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.pendingLoans}</div>
              <div className="metric-label">Préstamos Pendientes</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.approvedCards}</div>
              <div className="metric-label">Tarjetas Aprobadas</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.pendingCards}</div>
              <div className="metric-label">Tarjetas Pendientes</div>
            </div>
          </div>

          {/* Charts */}
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Préstamos por Estado / Día</h3>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={loanMetrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="_id" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#000" name="Total" strokeWidth={2} />
                  <Line type="monotone" dataKey="pending" stroke="#f39c12" name="Pendientes" />
                  <Line type="monotone" dataKey="approved" stroke="#2ecc71" name="Aprobados" />
                  <Line type="monotone" dataKey="rejected" stroke="#e74c3c" name="Rechazados" />
                  <Line type="monotone" dataKey="completed" stroke="#3498db" name="Completados" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Tarjetas por Estado / Día</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={cardMetrics.metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="_id" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total" fill="#667eea" name="Total" />
                  <Bar dataKey="pending" fill="#f39c12" name="Pendientes" />
                  <Bar dataKey="approved" fill="#2ecc71" name="Aprobados" />
                  <Bar dataKey="rejected" fill="#e74c3c" name="Rechazados" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Tarjetas por Banco</h3>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={cardMetrics.bankMetrics}
                    dataKey="total"
                    nameKey="_id"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={(entry) => `${entry._id}: ${entry.total}`}
                  >
                    {cardMetrics.bankMetrics.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Leads */}
          <div className="recent-leads-section">
            <div className="section-header">
              <h3>Leads Recientes</h3>
              <Link to="/leads" className="view-all-link">Ver todos</Link>
            </div>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Telefono</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-row">
                        No hay leads recientes
                      </td>
                    </tr>
                  ) : (
                    recentLeads.map((lead) => (
                      <tr key={lead._id}>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor:
                                lead.type === "loan"
                                  ? "#667eea"
                                  : lead.type === "card"
                                  ? "#764ba2"
                                  : "#3498db",
                            }}
                          >
                            {lead.type === "loan"
                              ? "Prestamo"
                              : lead.type === "card"
                              ? "Tarjeta"
                              : "Contacto"}
                          </span>
                        </td>
                        <td>
                          {lead.firstName} {lead.lastName}
                        </td>
                        <td>{lead.email}</td>
                        <td>{lead.phone}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor:
                                lead.status === "new"
                                  ? "#2ecc71"
                                  : lead.status === "contacted"
                                  ? "#f39c12"
                                  : lead.status === "qualified"
                                  ? "#3498db"
                                  : lead.status === "rejected"
                                  ? "#e74c3c"
                                  : "#95a5a6",
                            }}
                          >
                            {lead.status === "new"
                              ? "Nuevo"
                              : lead.status === "contacted"
                              ? "Contactado"
                              : lead.status === "qualified"
                              ? "Calificado"
                              : lead.status === "rejected"
                              ? "Rechazado"
                              : "Archivado"}
                          </span>
                        </td>
                        <td>{new Date(lead.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
